import { CollectionViewer } from '@angular/cdk/collections';
import { BehaviorSubject } from 'rxjs';
import { WorkstationService } from 'src/app/Services/Workstation/workstation.service';
import {
  getWorkstationAmenityFilterIconName,
  getWorkstationAmenityFilterSvgIconName,
  getWorkstationAmenityLabel,
} from 'src/app/utils/workstation-amenity-display.util';
import { BaseDataSource } from './base.data-source';

export type AmenityFilterOption = {
  id: number;
  label: string;
  iconName: string;
  svgIconName?: string;
};

export class WorkstationSearchDataSource extends BaseDataSource {
  disableReserve: boolean = false;

  private workstationOrderMap: Map<number, number> = new Map<number, number>();
  private allResultsCache: any[] = [];
  private lastSearchParams: any = null;
  private currentPageNumber: number = 0;
  private currentPageSize: number = 10;
  private currentFavouriteIds: number[] = [];
  private selectedAmenityIds: number[] = [];
  private deskSearchText: string = '';
  private readonly maxFetchedSearchResults: number = 1000;

  private readonly favouritesInResultsCountSubject = new BehaviorSubject<number>(0);
  readonly favouritesInResultsCount$ = this.favouritesInResultsCountSubject.asObservable();

  private readonly availableAmenitiesSubject = new BehaviorSubject<AmenityFilterOption[]>([]);
  readonly availableAmenities$ = this.availableAmenitiesSubject.asObservable();

  constructor(private workstationService: WorkstationService) {
    super();
  }

  override clearTable(): void {
    super.clearTable();
    this.allResultsCache = [];
    this.lastSearchParams = null;
    this.currentPageNumber = 0;
    this.selectedAmenityIds = [];
    this.deskSearchText = '';
    this.favouritesInResultsCountSubject.next(0);
    this.availableAmenitiesSubject.next([]);
  }

  override disconnect(collectionViewer: CollectionViewer): void {
    super.disconnect(collectionViewer);
    this.favouritesInResultsCountSubject.complete();
    this.availableAmenitiesSubject.complete();
  }

  loadWorkstations(
    userId: number,
    startDate: Date,
    endDate: Date,
    recurrence: number,
    timeId: number,
    floorIds: number[],
    favouriteIds: number[],
    pageNumber = 0,
    pageSize = 10,
    isRepeat = false,
    disableReserve = false
  ): Promise<any> {
    this.disableReserve = disableReserve;
    this.currentPageNumber = pageNumber;
    this.currentPageSize = pageSize;
    this.currentFavouriteIds = favouriteIds ?? [];

    const currentSearchParams: string = JSON.stringify({
      userId,
      startDate,
      endDate,
      recurrence,
      timeId,
      floorIds,
      isRepeat,
    });
    const isNewSearch: boolean = this.lastSearchParams !== currentSearchParams;

    if (isNewSearch) {
      this.lastSearchParams = currentSearchParams;
      this.selectedAmenityIds = [];
      this.deskSearchText = '';

      return new Promise((resolve) => {
        this.workstationService
          .findAvailable(
            userId,
            startDate,
            endDate,
            recurrence,
            timeId,
            floorIds,
            0,
            this.maxFetchedSearchResults,
            isRepeat
          )
          .subscribe((ws) => {
            ws = ws.value;
            const allResults: any[] = this.finalizeResults(ws.result ?? []);

            this.workstationOrderMap.clear();
            allResults.forEach((w: any, index: number) =>
              this.workstationOrderMap.set(w.id, index)
            );

            this.allResultsCache = allResults;
            this.availableAmenitiesSubject.next(this.buildAvailableAmenities(allResults));
            this.emitMergedResults(this.currentFavouriteIds, pageNumber, pageSize);

            resolve(this.allResultsCache.length);
          });
      });
    } else {
      return new Promise((resolve) => {
        this.emitMergedResults(this.currentFavouriteIds, pageNumber, pageSize);
        resolve(this.allResultsCache.length);
      });
    }
  }

  reSortFavourites(favouriteIds: number[]): void {
    this.currentFavouriteIds = favouriteIds ?? [];
    if (this.allResultsCache.length > 0) {
      this.emitMergedResults(
        this.currentFavouriteIds,
        this.currentPageNumber,
        this.currentPageSize
      );
    }
  }

  /** True when the current location/floor/date search returned desks before client-side filters */
  get hasUnfilteredResults(): boolean {
    return this.allResultsCache.length > 0;
  }

  /**
   * Filters the full cached result set by desk name (partial, case-insensitive,
   * surrounding spaces ignored) and resets to the first page
   */
  setDeskSearch(searchText: string, pageSize: number = this.currentPageSize): void {
    this.deskSearchText = (searchText ?? '').trim().toLowerCase();
    this.currentPageNumber = 0;
    this.currentPageSize = pageSize;
    this.emitMergedResults(this.currentFavouriteIds, this.currentPageNumber, this.currentPageSize);
  }

  setFeatureFilter(amenityIds: number[], pageSize: number = this.currentPageSize): void {
    this.selectedAmenityIds = amenityIds ?? [];
    this.currentPageNumber = 0;
    this.currentPageSize = pageSize;
    this.emitMergedResults(this.currentFavouriteIds, this.currentPageNumber, this.currentPageSize);
  }

  private buildAvailableAmenities(rows: any[]): AmenityFilterOption[] {
    const byId = new Map<number, AmenityFilterOption>();

    (rows ?? []).forEach((row: any) => {
      (row?.attributes ?? []).forEach((attribute: any) => {
        const amenity = attribute?.workstationAmenity;
        if (amenity?.id != null && !byId.has(amenity.id)) {
          byId.set(amenity.id, {
            id: amenity.id,
            label: getWorkstationAmenityLabel(amenity),
            iconName: getWorkstationAmenityFilterIconName(amenity),
            svgIconName: getWorkstationAmenityFilterSvgIconName(amenity),
          });
        }
      });
    });

    return Array.from(byId.values()).sort((a, b) => a.id - b.id);
  }

  private rowMatchesDeskSearch(row: any): boolean {
    if (!this.deskSearchText) {
      return true;
    }
    return String(row?.number ?? '')
      .toLowerCase()
      .includes(this.deskSearchText);
  }

  private rowMatchesFeatureFilter(row: any): boolean {
    if (this.selectedAmenityIds.length === 0) {
      return true;
    }

    const rowAmenityIds = new Set<number>(
      (row?.attributes ?? [])
        .map((attribute: any) => attribute?.workstationAmenity?.id)
        .filter((id: any) => id != null)
    );

    return this.selectedAmenityIds.every((id) => rowAmenityIds.has(id));
  }

  /**
   * Drops the API-provided "Book Any Available Workstation" placeholder rows (id 0)
   * so only real workstations are shown in the search results.
   */
  private finalizeResults(results: any[]): any[] {
    return (results ?? []).filter((workstation: any) => workstation?.id !== 0);
  }

  private emitMergedResults(favouriteIds: number[], pageNumber: number, pageSize: number): void {
    const favouriteSet = new Set(favouriteIds ?? []);

    const sortByDeskOrder = (a: any, b: any) =>
      (this.workstationOrderMap.get(a.id) ?? 0) - (this.workstationOrderMap.get(b.id) ?? 0);

    const filteredCache = this.allResultsCache.filter(
      (w) => this.rowMatchesFeatureFilter(w) && this.rowMatchesDeskSearch(w)
    );

    const favWorkstations = filteredCache
      .filter((w) => favouriteSet.has(w.id))
      .sort(sortByDeskOrder);

    const nonFavWorkstations = filteredCache
      .filter((w) => !favouriteSet.has(w.id))
      .sort(sortByDeskOrder);

    const merged = [...favWorkstations, ...nonFavWorkstations];

    this.favouritesInResultsCountSubject.next(favWorkstations.length);
    this.setDataCount(merged.length);

    const start = pageNumber * pageSize;
    const end = start + pageSize;
    this.subject.next(merged.slice(start, end));
  }
}
